import { useState } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { useCalendar } from "../calendar-context";
import { cn } from "@/lib/utils";
import type { TimeRange } from "@/lib/calendar-types";

export default function MonthlyView() {
  const {
    currentDate,
    events,
    selectedRanges,
    onAddTimeRange,
    onEditEvent,
    onSlotClick,
  } = useCalendar();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Get all days to display in the month view (including days from prev/next months)
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });

  // Group days into weeks
  const weeks: Date[][] = [];
  let week: Date[] = [];

  calendarDays.forEach((day, i) => {
    week.push(day);
    if (i % 7 === 6) {
      weeks.push(week);
      week = [];
    }
  });

  const handleDateClick = (date: Date) => {
    // If onSlotClick is provided, call it and return
    if (onSlotClick) {
      onSlotClick(date);
      return;
    }

    // Check if this date is already selected
    const isAlreadySelected = selectedRanges.some(
      (range) =>
        isSameDay(range.startDate, date) &&
        format(range.startDate, "HH:mm") === "09:00" &&
        format(range.endDate, "HH:mm") === "17:00",
    );

    if (isAlreadySelected) {
      // If already selected, do nothing (the toggle behavior is handled in the parent component)
      setSelectedDate(null);
    } else {
      setSelectedDate(date);
    }

    // Create a time range for the selected date (9 AM to 5 PM)
    const startDate = new Date(date);
    startDate.setHours(9, 0, 0, 0);

    const endDate = new Date(date);
    endDate.setHours(17, 0, 0, 0);

    const timeRange: TimeRange = {
      id: crypto.randomUUID(),
      startDate,
      endDate,
      dayName: format(date, "EEEE"),
    };

    onAddTimeRange(timeRange);
  };

  const getEventsForDay = (day: Date) => {
    return events.filter((event) => {
      const eventStart = new Date(event.start);
      return isSameDay(eventStart, day);
    });
  };

  const isDayInSelectedRange = (day: Date) => {
    return selectedRanges.some((range) => isSameDay(range.startDate, day));
  };

  // Limit the number of events to show per day
  const MAX_EVENTS_PER_DAY = 3;

  return (
    <div className="monthly-view h-[600px] overflow-y-auto">
      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-b">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div key={day} className="p-2 text-center font-medium">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 grid-rows-6 h-full">
        {weeks.flat().map((day, i) => {
          const dayEvents = getEventsForDay(day);
          const isToday = isSameDay(day, new Date());
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isSelected = isDayInSelectedRange(day);

          return (
            <div
              key={i}
              className={cn(
                "border-b border-r p-1 min-h-[100px] relative",
                !isCurrentMonth && "bg-muted",
                isSelected && "bg-primary/10",
                "hover:bg-muted cursor-pointer",
              )}
              onClick={() => handleDateClick(day)}
            >
              <div className="flex justify-between items-center">
                <div
                  className={cn(
                    "w-8 h-8 flex items-center justify-center rounded-full",
                    isToday && "bg-primary text-primary-foreground font-bold",
                  )}
                >
                  {format(day, "d")}
                </div>
                {i < 7 && (
                  <div className="text-xs text-muted-foreground">
                    {format(day, "MMM")}
                  </div>
                )}
              </div>

              {/* Events */}
              <div className="mt-1 space-y-1">
                {dayEvents.slice(0, MAX_EVENTS_PER_DAY).map((event) => (
                  <div
                    key={event.id}
                    className="text-xs p-1 rounded truncate cursor-pointer"
                    style={{
                      backgroundColor: event.color
                        ? `${event.color}20`
                        : "#4f46e520",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditEvent(event);
                    }}
                  >
                    <div className="flex items-center">
                      <div
                        className="w-2 h-2 rounded-full mr-1 flex-shrink-0"
                        style={{
                          backgroundColor: event.color || "var(--primary)",
                        }}
                      />
                      <span className="truncate">{event.title}</span>
                    </div>
                  </div>
                ))}

                {dayEvents.length > MAX_EVENTS_PER_DAY && (
                  <div className="text-xs text-muted-foreground pl-1">
                    +{dayEvents.length - MAX_EVENTS_PER_DAY} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
